import {
    EntryTypeHeaderCenterContent,
    EntryTypeHeaderRightContent,
} from '@template/ui/EditEntryTypePage/EditEntryTypePageHeaderContent';
import { useCallback } from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import BurgerIcon from './burger.svg';
import { observer } from 'mobx-react';
import style from '@core/Page/Header/Header.module.scss';
import { AllKeyOf } from 'chaika-common-types';

import { MeatballButton, MeatballModifier, MeatballThemes } from '@components/ActionButtons/MeatballButton';
import { CommonTestAttributes } from 'chaika-test-attributes';
import { store } from '@store';
import { HeaderComponents } from '@core/Page/Header/types';
import { TabCenter } from '@common/TabsNavigationPage/TabsNavigationPageHeaderContent/Center';
import { NavigationTabNew } from '@components/NavigationTabNew';
import { BackButton } from '@core/Page/Header/BackButton';
import { EhrHeaderCenterContent } from '@patient/ui/EhrPage/EhrHeaderContent';
// eslint-disable-next-line no-restricted-imports -- legacy-format entry header, rendered only for legacy entry routes
import { EntryHeaderCenterContent, EntryHeaderBottomContent } from '@patient/ui/LegacyEntryPage/EntryHeaderContent';
// eslint-disable-next-line no-restricted-imports -- legacy-format entry header action, rendered only for legacy entry routes
import { ActionButton as EntryActionButton } from '@patient/ui/LegacyEntryPage/EntryHeaderContent/Bottom/ActionButton';
import { ScheduleHeaderCenterContent } from '@schedule/ui/ScheduleV2Page/components/ScheduleHeaderContent';
import { DashboardHeaderBottom } from '@dashboard/ui/components/DashboardHeaderBottom';
import {
    BlockHeaderCenterContent,
    BlockHeaderRightContent,
} from '@template/ui/EditBlockPage/EditBlockPageHeaderContent';
import { OrgHeaderRightContent } from '@organization/ui/LegalEntityPage/pages/PlanListTemplatePage/Right';
import { SoapEntryHeaderRight } from '@patient/ui/SoapEntryPage/components/EntryHeader/SoapEntryHeaderRight';

export const HeaderCompontDic: AllKeyOf<HeaderComponents, React.ComponentType<any>> = {
    [HeaderComponents.CenterTabNavigation]: TabCenter,
    [HeaderComponents.NavigationTab]: NavigationTabNew,
    [HeaderComponents.BackButton]: BackButton,
    [HeaderComponents.EhrCenter]: EhrHeaderCenterContent,
    [HeaderComponents.EntryCenter]: EntryHeaderCenterContent,
    [HeaderComponents.EntryActionButton]: EntryActionButton,
    [HeaderComponents.EntryBottom]: EntryHeaderBottomContent,
    [HeaderComponents.ScheduleCenter]: ScheduleHeaderCenterContent,
    [HeaderComponents.BlockCenter]: BlockHeaderCenterContent,
    [HeaderComponents.BlockRight]: BlockHeaderRightContent,
    [HeaderComponents.OrgRightContent]: OrgHeaderRightContent,
    [HeaderComponents.SoapRightContent]: SoapEntryHeaderRight,
    [HeaderComponents.EntryTypeRight]: EntryTypeHeaderRightContent,
    [HeaderComponents.EntryTypeCenter]: EntryTypeHeaderCenterContent,
    [HeaderComponents.DashboardHeaderBottom]: DashboardHeaderBottom,
};

export const Header = observer(() => {
    const {
        app: {
            rightMenu,
            leftMenu,
            headerView: { isHide: hideHeader, leftContent, centerContent, bottomContent, rightContent, actions },
        },
        viewPort,
    } = store;
    const isRightMenuOpen = rightMenu.isShowing;
    const isMobileSize = viewPort.isMobileSize;
    const isTable = viewPort.isTableSize;

    const onOpenMenuClick = useCallback(() => {
        leftMenu.toggleIsLeftMenuOpen();
    }, [leftMenu]);

    function renderContent(content?: { component: HeaderComponents; props?: object }): React.ReactNode {
        if (!content) {
            return <div />;
        }
        const { component, props } = content;

        const Component = HeaderCompontDic[component];
        return <Component {...props} />;
    }

    return (
        <div
            className={classnames([style.root, isRightMenuOpen && style.root_rightMenuOpen, hideHeader && style.hide])}
        >
            <div className={style.leftContent}>
                {isTable ? openMenuButton(onOpenMenuClick) : renderContent(leftContent)}
            </div>
            <div className={style.centerContent}>{renderContent(centerContent)}</div>
            <div className={style.rightContent}>
                {!isEmpty(actions) && (
                    <div className={style.actions}>
                        <MeatballButton
                            withIcon={isMobileSize}
                            label={isMobileSize ? undefined : 'action'}
                            theme={MeatballThemes.Secondary}
                            modifiers={isMobileSize ? [] : [MeatballModifier.Bigger]}
                            items={actions}
                            dataTest={CommonTestAttributes.ActionsButton}
                        />
                    </div>
                )}
                {renderContent(rightContent)}
            </div>
            {bottomContent && <div className={style.bottomContent}>{renderContent(bottomContent)}</div>}
        </div>
    );
});

function openMenuButton(onOpenMenuClick: () => void) {
    return (
        <button
            className={style.openMenuButton}
            onClick={onOpenMenuClick}
        >
            <div className={style.openMenuIcon}>
                <BurgerIcon />
            </div>
        </button>
    );
}
